import { useEffect, useState, useRef } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import axiosClient from '../../../../utils/axios';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';
import { setPostDataAttribute } from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { LomavisMoreInfo } from '../../../../lomavisSharedUi/atoms/tooltips/LomavisMoreInfo';

interface LocationItem {
  fb_id: string;
  name: string;
}

interface TypeaheadLocationSelectionProps {
  platform: string;
  nameKey: string;
  idKey: string;
}

export const TypeaheadLocationSelection = ({ platform, nameKey, idKey }: TypeaheadLocationSelectionProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const selectedLocation = useSelector(
    (state: any) => state.lomavisCreator.postData[platform]
  );
  const [searchList, setSearchList] = useState<LocationItem[]>([]);
  const { place_search_urls: placeSearchUrls } = useSelector(
    (state: any) => state.lomavisCreator.lomavisCreatorConfig
  );

  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const DEBOUNCE_DELAY = 800; // milliseconds

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

    const searchForPlaces = (query: string): void => {
        setIsLoading(true);

        // Clear previous timeout if exists
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Cancel previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller
        abortControllerRef.current = new AbortController();

        debounceRef.current = setTimeout(() => {
            const url = placeSearchUrls[platform];
            if (!url) {
                setIsLoading(false);
                return;
            }

            const searchUrl = `${String(url)}${query.length > 2 ? `?q=${query}` : ''}`;

      axiosClient
        .get(searchUrl, { signal: abortControllerRef.current?.signal })
        .then((res) => {
          const data = res.data.results;
          // Filter out duplicate entries based on fb_id
          const uniqueData = data.reduce((acc: any[], item: any) => {
            if (!acc.some((existingItem) => existingItem.id === item.id)) {
              acc.push(item);
            }
            return acc;}, []);

          setSearchList(
            uniqueData.map((item: any) => ({
              fb_id: String(item.id),
              name: item.text
            }))
          );
          setIsLoading(false);
        })
        .catch((error) => {
          // Don't log aborted requests as errors
                    if (error.name !== 'AbortError') {
                        console.error('Error fetching places:', error);
          }setSearchList([]);
          setIsLoading(false);
        });
    }, DEBOUNCE_DELAY); // Adjust debounce time as needed
  };

  return (
    <div>
      <span className="align-items-center">
        <i className="fa-duotone fa-map-marker-alt me-2"></i>
        {t('lomavisCreator.additionalOptions.addLocation')}
        <LomavisMoreInfo
          text={t('lomavisCreator.additionalOptions.locationSelectionTooltip')}
        />
      </span>
      <Typeahead
        dropup={true} // This will make the dropdown go up
        disabled={!placeSearchUrls[platform]}
        id="tagged-users"
        labelKey="name"
        positionFixed={true}
        minLength={3}
        selected={selectedLocation[idKey] ? [{ fb_id: selectedLocation[idKey], name: selectedLocation[nameKey] }] : []}
        options={searchList}
        placeholder={t('lomavisCreator.additionalOptions.pleaseSelectLocation')}
        emptyLabel={
          !isLoading && searchList?.length > 0
            ? t('lomavisCreator.additionalOptions.noResultsFound')
            : ''
        } // Show only when not loading
        isLoading={isLoading}
        filterBy={() => true}
        onInputChange={(e) => {
          if (e.length > 3) {
            searchForPlaces(e);
          }
        }}
        onChange={(selected) => {
          if (selected.length === 0) {
            // remove selected location
            dispatch(
              setPostDataAttribute({
                platform: platform,
                attribute: idKey,
                value: null
              })
            );
            dispatch(
              setPostDataAttribute({
                platform: platform,
                attribute: nameKey,
                value: null
              })
            );
            return;
          }

          const selectedItem = selected[0] as LocationItem;

          dispatch(
            setPostDataAttribute({
              platform: platform,
              attribute: idKey,
              value: selectedItem?.fb_id
            })
          );
          dispatch(
            setPostDataAttribute({
              platform: platform,
              attribute: nameKey,
              value: selectedItem?.name
            })
          );
        }}
      />
      {!placeSearchUrls[platform] && (
        <span className="text-muted">
          {t('lomavisCreator.additionalOptions.locationSearchNotAvailable')}
        </span>
      )}
      <div className="d-flex flex-row flex-end">
        <span
          className="text-muted cursor-pointer flex-end"
          onClick={() => {
            dispatch(
              setPostDataAttribute({
                platform: platform,
                attribute: idKey,
                value: null
              })
            );
            dispatch(
              setPostDataAttribute({
                platform: platform,
                attribute: nameKey,
                value: null
              })
            );
          }}
        >
          {t('lomavisCreator.additionalOptions.clear_selection')}
        </span>
      </div>
    </div>
  );
};
